import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Plus, Trash2, Edit2, Calendar, Bell } from 'lucide-react';
import { ReminderField, REMINDER_OFFSETS, getBrowserTimezone } from '../components/ReminderField';

const fmtTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const offsetLabel = (v) => REMINDER_OFFSETS.find((o) => o.value === v)?.label;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOAL_TYPES = [
  { id: 'daily', label: 'Daily Goals' },
  { id: 'weekly', label: 'Weekly Goals' },
  { id: 'monthly', label: 'Monthly Goals' },
  { id: 'quarterly', label: 'Quarterly Goals' },
  { id: 'yearly', label: 'Yearly Goals' },
];

export const GoalsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [goals, setGoals] = useState([]);
  const [activeTab, setActiveTab] = useState('daily');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goal_type: 'daily',
    due_date: '',
    due_time: '',
    reminder_offset: 'none',
  });

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/goals`, getAuthHeaders());
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        goal_type: editingGoal ? formData.goal_type : activeTab,
        due_date: formData.due_date || '',
        due_time: formData.due_date ? (formData.due_time || '09:00') : '',
        reminder_offset: formData.due_date ? (formData.reminder_offset || 'none') : 'none',
        timezone: getBrowserTimezone(),
      };
      
      if (editingGoal) {
        await axios.put(`${API}/goals/${editingGoal.goal_id}`, payload, getAuthHeaders());
        toast.success('Goal updated!');
      } else {
        await axios.post(`${API}/goals`, payload, getAuthHeaders());
        toast.success('Goal created!');
      }
      fetchGoals();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const toggleGoalComplete = async (goal) => {
    try {
      await axios.put(`${API}/goals/${goal.goal_id}`, { completed: !goal.completed }, getAuthHeaders());
      setGoals(goals.map(g => g.goal_id === goal.goal_id ? { ...g, completed: !g.completed } : g));
    } catch (error) {
      toast.error('Failed to update goal');
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`, getAuthHeaders());
      setGoals(goals.filter(g => g.goal_id !== goalId));
      toast.success('Goal deleted');
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    // legacy goals (date-only reminder) carry their old reminder moment over as an at-time reminder
    const legacy = !goal.reminder_offset && goal.reminder_date;
    const dueDate = goal.due_date || (legacy ? goal.reminder_date : '');
    setFormData({
      title: goal.title,
      description: goal.description || '',
      goal_type: goal.goal_type,
      due_date: dueDate,
      due_time: goal.due_time || (legacy && goal.reminder_time) || (dueDate ? '09:00' : ''),
      reminder_offset: goal.reminder_offset || (legacy ? 'at_time' : 'none'),
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      goal_type: activeTab,
      due_date: '',
      due_time: '',
      reminder_offset: 'none',
    });
    setEditingGoal(null);
  };

  const filteredGoals = goals.filter(g => g.goal_type === activeTab);

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="goals-tab">
      {/* Header */}
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-2xl mb-3">
          <Target className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </div>
        <h1 className="text-2xl font-outfit font-bold text-amber-600 dark:text-amber-400">Goals</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
          The Goals tab is where you turn your dreams into clear, trackable targets with due dates and reminders you set to keep you moving forward.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-5 bg-amber-50 dark:bg-amber-900/20">
          {GOAL_TYPES.map((type) => (
            <TabsTrigger 
              key={type.id} 
              value={type.id}
              className="text-xs data-[state=active]:bg-amber-500 data-[state=active]:text-white"
              data-testid={`goal-tab-${type.id}`}
            >
              {type.label.split(' ')[0]}
            </TabsTrigger>
          ))}
        </TabsList>

        {GOAL_TYPES.map((type) => (
          <TabsContent key={type.id} value={type.id} className="mt-4">
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-amber-700 dark:text-amber-300">
                  {type.label}
                </CardTitle>
                <Dialog open={isDialogOpen && activeTab === type.id} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid={`add-goal-btn-${type.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Goal
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingGoal ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Goal Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="What do you want to achieve?"
                          data-testid="goal-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Add more details..."
                          data-testid="goal-description-input"
                        />
                      </div>
                      <ReminderField
                        value={{ date: formData.due_date, time: formData.due_time, offset: formData.reminder_offset }}
                        onChange={(v) => setFormData({ ...formData, due_date: v.date, due_time: v.time, reminder_offset: v.offset })}
                        accent="amber"
                        dateLabel="Deadline"
                        timeLabel="Deadline time"
                        testPrefix="goal-reminder"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="save-goal-btn">
                          {editingGoal ? 'Update Goal' : 'Create Goal'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[400px]">
                  <AnimatePresence mode="popLayout">
                    {filteredGoals.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No {type.label.toLowerCase()} yet. Add your first goal!
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {filteredGoals.map((goal, index) => (
                          <motion.li
                            key={goal.goal_id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: index * 0.05 }}
                            className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
                              goal.completed 
                                ? 'bg-amber-50/50 dark:bg-amber-900/10' 
                                : 'bg-white dark:bg-slate-800 border border-amber-100 dark:border-amber-900'
                            }`}
                          >
                            <Checkbox
                              checked={goal.completed}
                              onCheckedChange={() => toggleGoalComplete(goal)}
                              className="mt-1 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                              data-testid={`goal-checkbox-${index}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${goal.completed ? 'line-through text-muted-foreground' : ''}`}>
                                {goal.title}
                              </p>
                              {goal.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {goal.description}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                                {goal.due_date && (
                                  <span className="flex items-center gap-1" data-testid={`goal-deadline-${index}`}>
                                    <Calendar className="w-3 h-3" />
                                    Due: {new Date(`${goal.due_date}T00:00:00`).toLocaleDateString()}
                                    {goal.due_time && ` at ${fmtTime12(goal.due_time)}`}
                                  </span>
                                )}
                                {goal.reminder_offset && goal.reminder_offset !== 'none' ? (
                                  <span className="flex items-center gap-1 text-amber-600" data-testid={`goal-reminder-label-${index}`}>
                                    <Bell className="w-3 h-3" />
                                    {offsetLabel(goal.reminder_offset)}
                                  </span>
                                ) : goal.reminder_date && (
                                  <span className="flex items-center gap-1 text-amber-600">
                                    <Bell className="w-3 h-3" />
                                    {new Date(`${goal.reminder_date}T00:00:00`).toLocaleDateString()}
                                    {goal.reminder_time && ` ${goal.reminder_time}`}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openEditDialog(goal)}
                                className="h-8 w-8 text-amber-600 hover:bg-amber-100"
                                data-testid={`edit-goal-${index}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteGoal(goal.goal_id)}
                                className="h-8 w-8 text-red-500 hover:bg-red-100"
                                data-testid={`delete-goal-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </motion.li>
                        ))}
                      </ul>
                    )}
                  </AnimatePresence>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default GoalsTab;
